import React from 'react';

/**
 * Common Skeleton Loader Utility
 * Reusable skeleton components for all report pages
 */
export class SkeletonLoader {
  /**
   * Base skeleton loader with shimmer effect
   * @param {Object} props - Component props
   * @param {string} props.classNames - Additional CSS classes
   * @param {React.ReactNode} props.children - Child components
   * @param {string} props.width - Width of skeleton
   * @param {string} props.height - Height of skeleton
   * @param {string} props.borderRadius - Border radius
   */
  static Base = ({ classNames = '', children = null, width = '100%', height = '12px', borderRadius = '4px' }) => {
    return (
      <div 
        className={`skeleton-loader ${classNames}`}
        style={{ width, height, borderRadius }}
      >
        <div className="skeleton-shimmer"></div>
        {children}
      </div>
    );
  };

  /**
   * Card skeleton loader
   * @param {Object} props - Component props
   * @param {string} props.classNames - Additional CSS classes
   * @param {React.ReactNode} props.children - Child components
   */
  static Card = ({ classNames = '', children = null }) => {
    return (
      <div className={`skeleton-card ${classNames}`}>
        <div className="skeleton-shimmer"></div>
        {children}
      </div>
    );
  };

  /**
   * Text skeleton loader
   * @param {Object} props - Component props
   * @param {string} props.classNames - Additional CSS classes
   * @param {string} props.width - Width of skeleton
   * @param {string} props.height - Height of skeleton
   */
  static Text = ({ classNames = '', width = '100%', height = '12px' }) => {
    return (
      <div 
        className={`skeleton-loader ${classNames}`}
        style={{ width, height }}
      >
        <div className="skeleton-shimmer"></div>
      </div>
    );
  };

  /**
   * Circle skeleton loader
   * @param {Object} props - Component props
   * @param {string} props.classNames - Additional CSS classes
   * @param {string} props.size - Size of circle
   */
  static Circle = ({ classNames = '', size = '40px' }) => {
    return (
      <div 
        className={`skeleton-loader skeleton-circle ${classNames}`}
        style={{ width: size, height: size, borderRadius: '50%' }}
      >
        <div className="skeleton-shimmer"></div>
      </div>
    );
  };

  /**
   * Rectangle skeleton loader
   * @param {Object} props - Component props
   * @param {string} props.classNames - Additional CSS classes
   * @param {string} props.width - Width of skeleton
   * @param {string} props.height - Height of skeleton
   */
  static Rectangle = ({ classNames = '', width = '100%', height = '100px' }) => {
    return (
      <div 
        className={`skeleton-loader ${classNames}`}
        style={{ width, height }}
      >
        <div className="skeleton-shimmer"></div>
      </div>
    );
  };

  /**
   * Table row skeleton loader
   * @param {Object} props - Component props
   * @param {string} props.classNames - Additional CSS classes
   * @param {number} props.columns - Number of columns
   */
  static TableRow = ({ classNames = '', columns = 4 }) => {
    return (
      <tr className={`skeleton-table-row ${classNames}`}>
        {Array.from({ length: columns }).map((_, index) => (
          <td key={index} className="p-2">
            <SkeletonLoader.Text width="80%" height="16px" />
          </td>
        ))}
      </tr>
    );
  };

  /**
   * Chart skeleton loader with customizable chart types
   * @param {Object} props - Component props
   * @param {string} props.classNames - Additional CSS classes
   * @param {string} props.width - Width of chart
   * @param {string} props.height - Height of chart
   * @param {string} props.chartType - Type of chart (line, bar, pie, area)
   * @param {number} props.dataPoints - Number of data points to show
   */
  static Chart = ({ 
    classNames = '', 
    width = '100%', 
    height = '300px', 
    chartType = 'line',
    dataPoints = 7
  }) => {
    const renderChartSkeleton = () => {
      switch (chartType) {
        case 'line':
          return <SkeletonLoader.LineChart dataPoints={dataPoints} />;
        case 'bar':
          return <SkeletonLoader.BarChart dataPoints={dataPoints} />;
        case 'pie':
          return <SkeletonLoader.PieChart />;
        case 'area':
          return <SkeletonLoader.AreaChart dataPoints={dataPoints} />;
        default:
          return <SkeletonLoader.LineChart dataPoints={dataPoints} />;
      }
    };

    return (
      <div className={`skeleton-chart ${classNames}`} style={{ width, height }}>
        <div className="skeleton-shimmer"></div>
        {renderChartSkeleton()}
        {/* <div className="position-absolute" style={{ bottom: '20px', left: '20px' }}>
          <SkeletonLoader.Text width="80px" height="12px" />
        </div> */}
      </div>
    );
  };

  /**
   * Simple mountain-like line chart skeleton
   * @param {Object} props - Component props
   * @param {number} props.dataPoints - Number of data points
   */
  static LineChart = ({ dataPoints = 7 }) => {
    // Create a simple mountain-like line
    const points = Array.from({ length: dataPoints }, (_, i) => {
      const x = (i / (dataPoints - 1)) * 100;
      // Create a mountain shape: start high, dip in middle, rise at end
      let y;
      if (i <= 1) {
        y = 30; // Start high
      } else if (i <= 3) {
        y = 70; // Dip in middle
      } else {
        y = 40; // Rise at end
      }
      return { x, y };
    });

    const pathData = points.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x}% ${point.y}%`
    ).join(' ');

    return (
      <div className="skeleton-line-chart">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Simple mountain line */}
          <path
            d={pathData}
            stroke="#d0d0d0"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  };

  /**
   * Simple bar chart skeleton
   * @param {Object} props - Component props
   * @param {number} props.dataPoints - Number of bars
   */
  static BarChart = ({ dataPoints = 7 }) => {
    return (
      <div className="skeleton-bar-chart">
        <div className="d-flex align-items-end justify-content-between h-100" style={{ padding: '20px' }}>
          {Array.from({ length: dataPoints }).map((_, i) => {
            // Create a simple mountain pattern for bars
            let height;
            if (i <= 1) {
              height = 60; // Start high
            } else if (i <= 3) {
              height = 30; // Dip in middle
            } else {
              height = 50; // Rise at end
            }
            return (
              <div
                key={i}
                className="skeleton-bar"
                style={{
                  width: `${Math.max(8, 80 / dataPoints)}%`,
                  height: `${height}%`,
                  backgroundColor: '#d0d0d0',
                  borderRadius: '3px 3px 0 0'
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  /**
   * Pie chart skeleton
   */
  static PieChart = () => {
    return (
      <div className="skeleton-pie-chart d-flex align-items-center justify-content-center h-100">
        <div
          className="skeleton-circle"
          style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            backgroundColor: '#e0e0e0'
          }}
        >
          <div className="skeleton-shimmer"></div>
        </div>
      </div>
    );
  };

  /**
   * Clean area chart skeleton
   * @param {Object} props - Component props
   * @param {number} props.dataPoints - Number of data points
   */
  static AreaChart = ({ dataPoints = 7 }) => {
    // Create a simple, clean area shape
    const points = Array.from({ length: dataPoints }, (_, i) => {
      const x = (i / (dataPoints - 1)) * 100;
      const y = 30 + Math.sin(i * 0.5) * 20;
      return { x, y };
    });

    const areaPath = `M 0,80 L ${points.map(p => `${p.x},${p.y}`).join(' L ')} L 100,80 Z`;
    const linePath = `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`;

    return (
      <div className="skeleton-area-chart">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Clean area fill */}
          <path
            d={areaPath}
            fill="#e0e0e0"
            opacity="0.4"
          />
          {/* Simple line */}
          <path
            d={linePath}
            stroke="#d0d0d0"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  };
}

/**
 * Pre-built skeleton components for common use cases
 */
export const SkeletonComponents = {
  /**
   * Metrics card skeleton
   */
  MetricsCard: () => (
    <div className="bg-white rounded-8 py-2 px-1 d-flex align-items-start justify-content-between tar_tiles_shadow hei-63">
      <div className="d-flex flex-column w-100">
        <SkeletonLoader.Text classNames="hei-12 wid-60 mb-2" />
        <SkeletonLoader.Text classNames="hei-16 wid-40 mb-1" />
        {/* <SkeletonLoader.Text classNames="hei-10 wid-30" /> */}
      </div>
      <div className="d-flex flex-column align-items-end">
        <SkeletonLoader.Text classNames="hei-8 wid-20 mb-1" />
        {/* <SkeletonLoader.Text classNames="hei-6 wid-16" /> */}
      </div>
    </div>
  ),

  /**
   * Chart skeleton loader with customizable chart type
   */
  ChartLoader: ({ chartType = 'line', dataPoints = 7, classNames = '' }) => (
    <div className={`p-4 w-100 ${classNames}`}>
      {/* <div className="d-flex justify-content-between align-items-center mb-3 d-none">
        <SkeletonLoader.Text classNames="hei-16 wid-120" />
        <div className="d-flex gap-2">
          <SkeletonLoader.Text classNames="hei-8 wid-60" />
          <SkeletonLoader.Text classNames="hei-8 wid-40" />
        </div>
      </div> */}
      <SkeletonLoader.Chart chartType={chartType} dataPoints={dataPoints} />
    </div>
  ),

  /**
   * Animated trend line placeholder for charts
   */
  TrendChartLoader: ({ classNames = '' }) => (
    <div className={`p-4 w-100 ${classNames}`}>
      <div className="skeleton-chart position-relative" style={{ height: '320px' }}>
        <svg className="skeleton-trend-svg" width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          {Array.from({ length: 5 }).map((_, i) => (
            <line key={`g-${i}`} x1="0" y1={`${20 + i * 15}`} x2="100" y2={`${20 + i * 15}`} stroke="#eee" strokeWidth="0.4" />
          ))}
          {/* Primary sleek line */}
          <path
            className="skeleton-trend-path"
            d="M 0 72 C 14 64, 26 60, 36 61 C 46 62, 52 70, 60 54 C 68 42, 76 68, 84 44 C 90 30, 96 50, 100 40"
            fill="none"
          />
          {/* Secondary subtle line */}
          <path
            className="skeleton-trend-path skeleton-trend-path--secondary"
            d="M 0 68 C 12 60, 24 58, 34 59 C 44 60, 52 67, 58 51 C 66 39, 74 66, 82 42 C 90 32, 94 48, 100 37"
            fill="none"
          />
          {/* Tertiary subtle line */}
          <path
            className="skeleton-trend-path skeleton-trend-path--tertiary"
            d="M 0 75 C 16 66, 26 61, 38 62 C 48 63, 54 71, 62 55 C 70 45, 78 69, 86 46 C 92 34, 97 52, 100 42"
            fill="none"
          />
        </svg>
      </div>
    </div>
  ),

  /**
   * Line chart skeleton loader (most common)
   */
  LineChartLoader: ({ dataPoints = 7, classNames = '' }) => (
    <SkeletonComponents.ChartLoader chartType="line" dataPoints={dataPoints} classNames={classNames} />
  ),

  /**
   * Bar chart skeleton loader
   */
  BarChartLoader: ({ dataPoints = 7, classNames = '' }) => (
    <SkeletonComponents.ChartLoader chartType="bar" dataPoints={dataPoints} classNames={classNames} />
  ),

  /**
   * Area chart skeleton loader
   */
  AreaChartLoader: ({ dataPoints = 7, classNames = '' }) => (
    <SkeletonComponents.ChartLoader chartType="area" dataPoints={dataPoints} classNames={classNames} />
  ),

  /**
   * Pie chart skeleton loader
   */
  PieChartLoader: ({ classNames = '' }) => (
    <SkeletonComponents.ChartLoader chartType="pie" classNames={classNames} />
  ),

  /**
   * Calendar skeleton loader
   */
  CalendarLoader: () => (
    <div className="d-flex align-items-center justify-content-center p-4">
      <div className="skeleton-loader-container">
        <div className="d-flex align-items-center gap-3">
          <SkeletonLoader.Circle size="20px" />
          <div className="d-flex flex-column gap-2">
            <SkeletonLoader.Text width="120px" height="12px" />
            <SkeletonLoader.Text width="80px" height="10px" />
          </div>
          {/* <SkeletonLoader.Circle size="10px" /> */}
        </div>
      </div>
    </div>
  ),

  /**
   * Date picker skeleton loader
   */
  DatePickerLoader: () => (
    <div className="bg-white p-3 text-dark d-flex align-items-center f-w-500 f-15 cursor-pointer rounded-8 calender_style">
      <SkeletonLoader.Circle size="20px" classNames="mr-2" />
      <div className="d-flex flex-column gap-10">
        <div className="d-flex align-items-center">
          <SkeletonLoader.Text width="100px" height="12px" classNames="mr-1" />
          <SkeletonLoader.Text width="80px" height="12px" />
        </div>
      </div>
      <SkeletonLoader.Circle size="10px" classNames="ml-auto" />
    </div>
  ),

  /**
   * Table skeleton loader
   */
  TableLoader: ({ rows = 5, columns = 4, classNames = '' }) => (
    <div className={`skeleton-table-container ${classNames}`}>
      <table className="table">
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, index) => (
              <th key={index}>
                <SkeletonLoader.Text width="60%" height="14px" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <SkeletonLoader.TableRow key={rowIndex} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  ),

  /**
   * Report page skeleton loader
   */
  ReportPageLoader: () => (
    <div className="skeleton-report-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <SkeletonLoader.Text width="200px" height="24px" />
        <div className="d-flex gap-2">
          <SkeletonLoader.Text width="80px" height="32px" />
          <SkeletonLoader.Text width="100px" height="32px" />
        </div>
      </div>
      <div className="row">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="col-md-3 mb-3">
            <SkeletonComponents.MetricsCard />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <SkeletonComponents.ChartLoader />
      </div>
    </div>
  )
};

export default SkeletonLoader;
